import { DateField, ErrorState, Loading, PageHead, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MonthView } from '../calendar/MonthView';
import { Field, UserSelect } from '../crm/forms';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export interface CalendarData {
  holidays: { id: string; date: string; name: string; nameAr: string | null }[];
  timeOff: {
    id: string;
    userId: string;
    displayName: string;
    displayNameAr: string | null;
    startsOn: string;
    endsOn: string;
    note: string | null;
  }[];
  me: { userId: string; availability: 'available' | 'away' };
  canManageHolidays: boolean;
  canManageTimeOff: boolean;
}

export const useCalendar = () =>
  useQuery({ queryKey: ['calendar'], queryFn: () => api<CalendarData>('/v1/calendar'), staleTime: 30_000 });

/** A date-only value as an instant that is the same calendar day in every zone within ±12 h. */
const noonOf = (date: string) => new Date(`${date}T12:00:00Z`);

function useCalendarMutation<T>(fn: (v: T) => Promise<unknown>, onDone?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      void qc.invalidateQueries({ queryKey: ['service-levels'] });
      onDone?.();
    },
  });
}

/** Available / Away. Used in the header and on the Calendar page. */
export function AvailabilityToggle({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const cal = useCalendar();
  const set = useCalendarMutation((status: 'available' | 'away') =>
    api('/v1/me/availability', { method: 'PUT', body: JSON.stringify({ status }) }),
  );
  const status = cal.data?.me.availability;
  if (!status) return null;
  if (compact) {
    const next = status === 'available' ? 'away' : 'available';
    return (
      <button
        type="button"
        className={`status-pill is-${status}`}
        onClick={() => set.mutate(next)}
        disabled={set.isPending}
        aria-label={t('calendar.statusIs', { status: t(`calendar.status.${status}`) })}
        title={t('calendar.statusHint')}
        data-testid="availability-pill"
      >
        <span className="status-dot" aria-hidden="true" />
        {t(`calendar.status.${status}`)}
      </button>
    );
  }
  return (
    <div className="segmented" role="group" aria-label={t('calendar.myStatus')} data-testid="availability">
      {(['available', 'away'] as const).map((s) => (
        <button key={s} aria-pressed={status === s} onClick={() => set.mutate(s)} disabled={set.isPending}>
          {t(`calendar.status.${s}`)}
        </button>
      ))}
    </div>
  );
}

export function CalendarPage() {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const { session } = useAuth();
  const cal = useCalendar();
  const ar = i18n.language === 'ar' || i18n.language === 'ur';
  const name = (n: string, nAr: string | null) => (ar && nAr ? nAr : n);
  const range = (a: string, b: string) =>
    a === b ? fmt.date(noonOf(a)) : `${fmt.date(noonOf(a))} – ${fmt.date(noonOf(b))}`;

  const [off, setOff] = useState({ userId: '', startsOn: '', endsOn: '', note: '' });
  const addOff = useCalendarMutation(
    () =>
      api('/v1/time-off', {
        method: 'POST',
        body: JSON.stringify({
          ...(off.userId ? { userId: off.userId } : {}),
          startsOn: off.startsOn,
          endsOn: off.endsOn || off.startsOn,
          note: off.note.trim() || undefined,
        }),
      }),
    () => setOff({ userId: '', startsOn: '', endsOn: '', note: '' }),
  );
  const removeOff = useCalendarMutation((id: string) => api(`/v1/time-off/${id}`, { method: 'DELETE' }));

  const [hol, setHol] = useState({ date: '', name: '', nameAr: '' });
  const addHol = useCalendarMutation(
    () =>
      api('/v1/holidays', {
        method: 'POST',
        body: JSON.stringify({ date: hol.date, name: hol.name, nameAr: hol.nameAr.trim() || null }),
      }),
    () => setHol({ date: '', name: '', nameAr: '' }),
  );
  const removeHol = useCalendarMutation((id: string) => api(`/v1/holidays/${id}`, { method: 'DELETE' }));

  if (cal.isLoading) return <Loading />;
  if (cal.isError) return <ErrorState error={cal.error} onRetry={() => void cal.refetch()} />;
  const d = cal.data!;
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: session?.tenant.defaults.timezone ?? 'Asia/Dubai',
  }).format(new Date());

  return (
    <>
      <PageHead eyebrow={t('nav.main')} title={t('calendar.title')} subtitle={t('calendar.subtitle')} />
      <MonthView data={d} today={today} />
      <div className="calendar-grid">
        <section className="card card-pad" aria-labelledby="cal-me">
          <h2 id="cal-me" className="section-title">
            {t('calendar.myStatus')}
          </h2>
          <AvailabilityToggle />
          <p className="faint" style={{ fontSize: 'var(--t-xs)', margin: '0.5rem 0 0' }}>
            {t('calendar.statusHint')}
          </p>

          <h3 className="section-title" style={{ marginTop: '1.25rem' }}>
            {t('calendar.addTimeOff')}
          </h3>
          <form
            className="form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              addOff.mutate(undefined);
            }}
            data-testid="time-off-form"
          >
            {d.canManageTimeOff && (
              <Field id="off-user" label={t('calendar.person')}>
                <UserSelect
                  id="off-user"
                  value={off.userId}
                  onChange={(v) => setOff({ ...off, userId: v })}
                />
              </Field>
            )}
            <Field id="off-from" label={t('calendar.from')}>
              <DateField
                id="off-from"
                required
                value={off.startsOn}
                onChange={(v) => setOff({ ...off, startsOn: v })}
              />
            </Field>
            <Field id="off-to" label={t('calendar.to')}>
              <DateField
                id="off-to"
                min={off.startsOn || undefined}
                value={off.endsOn}
                onChange={(v) => setOff({ ...off, endsOn: v })}
              />
            </Field>
            <Field id="off-note" label={t('calendar.note')} hint={t('calendar.noteHint')}>
              <input
                id="off-note"
                className="input"
                dir="auto"
                maxLength={300}
                value={off.note}
                onChange={(e) => setOff({ ...off, note: e.target.value })}
              />
            </Field>
            <div className="field" style={{ alignContent: 'end' }}>
              <button
                type="submit"
                className="btn btn-grad"
                disabled={addOff.isPending}
                style={{ justifySelf: 'start' }}
              >
                {t('calendar.addTimeOff')}
              </button>
            </div>
          </form>
          {addOff.isError && <ErrorState error={addOff.error} />}
        </section>

        <section className="card card-pad" aria-labelledby="cal-away" data-testid="who-is-away">
          <h2 id="cal-away" className="section-title">
            {t('calendar.whoIsAway')}
          </h2>
          <ul className="list-plain cal-list">
            {d.timeOff
              .filter((o) => o.endsOn >= today)
              .map((o) => {
                const now = o.startsOn <= today && o.endsOn >= today;
                const mine = o.userId === d.me.userId;
                return (
                  <li key={o.id} className={now ? 'is-now' : ''}>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <strong dir="auto">{name(o.displayName, o.displayNameAr)}</strong>
                      {now && <span className="badge badge-warning">{t('calendar.awayToday')}</span>}
                      <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                        {range(o.startsOn, o.endsOn)}
                        {o.note && (
                          <>
                            {' · '}
                            <span dir="auto">{o.note}</span>
                          </>
                        )}
                      </div>
                    </div>
                    {(mine || d.canManageTimeOff) && (
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => removeOff.mutate(o.id)}
                        aria-label={t('calendar.removeTimeOff', {
                          name: name(o.displayName, o.displayNameAr),
                        })}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </li>
                );
              })}
            {!d.timeOff.some((o) => o.endsOn >= today) && (
              <li className="faint">{t('calendar.nobodyAway')}</li>
            )}
          </ul>
        </section>

        <section className="card card-pad" aria-labelledby="cal-hol" data-testid="holidays">
          <h2 id="cal-hol" className="section-title">
            {t('sla.holidays')}
          </h2>
          <p className="faint" style={{ marginTop: 0, fontSize: 'var(--t-xs)' }}>
            {t('sla.holidaysHint')}
          </p>
          <ul className="list-plain cal-list">
            {d.holidays
              .filter((h) => h.date >= today)
              .map((h) => (
                <li key={h.id}>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <strong dir="auto">{name(h.name, h.nameAr)}</strong>
                    <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                      {fmt.date(noonOf(h.date))}
                    </div>
                  </div>
                  {d.canManageHolidays && (
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => removeHol.mutate(h.id)}
                      aria-label={t('sla.removeHoliday', { name: h.name })}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </li>
              ))}
            {!d.holidays.some((h) => h.date >= today) && <li className="faint">{t('sla.noHolidays')}</li>}
          </ul>
          {d.canManageHolidays && (
            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                addHol.mutate(undefined);
              }}
            >
              <Field id="hol-date" label={t('sla.holidayDate')}>
                <DateField
                  id="hol-date"
                  required
                  value={hol.date}
                  onChange={(v) => setHol({ ...hol, date: v })}
                />
              </Field>
              <Field id="hol-name" label={t('sla.holidayName')}>
                <input
                  id="hol-name"
                  className="input"
                  required
                  maxLength={120}
                  value={hol.name}
                  onChange={(e) => setHol({ ...hol, name: e.target.value })}
                />
              </Field>
              <Field id="hol-name-ar" label={t('sla.holidayNameAr')}>
                <input
                  id="hol-name-ar"
                  className="input"
                  dir="rtl"
                  lang="ar"
                  maxLength={120}
                  value={hol.nameAr}
                  onChange={(e) => setHol({ ...hol, nameAr: e.target.value })}
                />
              </Field>
              <div className="field" style={{ alignContent: 'end' }}>
                <button
                  type="submit"
                  className="btn"
                  disabled={addHol.isPending}
                  style={{ justifySelf: 'start' }}
                  data-testid="add-holiday"
                >
                  {t('sla.addHoliday')}
                </button>
              </div>
            </form>
          )}
          {(addHol.isError || removeHol.isError || removeOff.isError) && (
            <ErrorState error={addHol.error ?? removeHol.error ?? removeOff.error} />
          )}
        </section>
      </div>
    </>
  );
}
